'use client';
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable-next-line */
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { styled } from '@linaria/react';
import debounce from 'lodash.debounce';
import { useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { DESKTOP_AD_SIZE, MOBILE_AD_SIZE } from '../../constants/adSize';
import { DEVICE } from '../../constants/device';
import { refresh, unmountSlots } from '../../utils/googletag';

declare global {
  interface Window {
    googletag: typeof googletag;
  }
}

type ContainerProps = {
  width?: number;
  height?: number;
  mobileWidth?: number;
  mobileHeight?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  device?: string;
};

const Container = styled.div<ContainerProps>`
  width: ${({ width }) => `${width}px` || '100%'};
  height: ${({ height }) => (height ? `${height}px` : 'auto')};
  margin-top: ${props => props.marginTop || 0}px;
  margin-bottom: ${props => props.marginBottom || 0}px;
  margin-left: ${({ marginLeft }) => (marginLeft ? `${marginLeft}px` : 'auto')};
  margin-right: ${({ marginRight }) => (marginRight ? `${marginRight}px` : 'auto')};
  display: ${({ device }) => (device === DEVICE.MOBILE ? 'none' : 'block')};

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    width: ${({ mobileWidth }) => (mobileWidth ? `${mobileWidth}px` : 'auto')};
    height: ${({ mobileHeight }) => (mobileHeight ? `${mobileHeight}px` : 'auto')};
    display: block;
  }
`;

const getSize = (size: (string | number)[][]) => {
  const numberSize = size.filter(s => typeof s !== 'string') as number[][];
  const DEFAULT_SIZE = [0, 0];
  const [width, height] = numberSize.find(v => v[0] > SCREEN_SIZE.TABLET) || DEFAULT_SIZE;
  const [mobileWidth, mobileHeight] = numberSize.find(v => v[0] < SCREEN_SIZE.TABLET) || DEFAULT_SIZE;

  return [width, height, mobileWidth, mobileHeight];
};

type Profile = {
  name: string;
  path: string;
  size: (number | string)[][];
  className?: string;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
  device?: string;
};

export const DynamicAdSlot = ({ profile }: { profile: Profile }) => {
  const { name, size, path, marginTop, marginBottom, marginLeft, marginRight, device } = profile;
  const slotId = `div-gpt-ad-${name}`;
  const slot = useRef<googletag.Slot | null>(null);
  const [width, height, mobileWidth, mobileHeight] = getSize(size);

  const init = () => {
    const googletag = window.googletag || null;

    if (googletag?.apiReady) {
      // 響應式廣告: https://developers.google.com/publisher-tag/guides/ad-sizes?hl=zh-tw#responsive_ads
      // SCREEN_SIZE.DESKTOP 以上顯示 DESKTOP_AD_SIZE
      // SCREEN_SIZE.DESKTOP 以下顯示 MOBILE_AD_SIZE

      const mapping = googletag
        ?.sizeMapping()
        ?.addSize([SCREEN_SIZE.DESKTOP, 0], DESKTOP_AD_SIZE as googletag.GeneralSize)
        ?.addSize([0, 0], MOBILE_AD_SIZE as googletag.GeneralSize)
        ?.build();

      // 註冊廣告版位: https://developers.google.com/publisher-tag/guides/get-started?hl=zh-tw
      googletag.cmd?.push(function () {
        slot.current = googletag
          ?.defineSlot(path, size as googletag.GeneralSize, slotId)
          ?.defineSizeMapping(mapping as googletag.SizeMappingArray)
          ?.addService(googletag.pubads()) ?? null;
        googletag.enableServices();
      });

      // 顯示廣告
      googletag.display(slotId);
    }
  };

  const handleResize = debounce(() => {
    refresh();
  }, 500);

  useEffect(() => {
    init();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      unmountSlots(slot?.current);
    };
  }, []);

  return (
    <Container
      id={slotId}
      marginTop={marginTop}
      marginBottom={marginBottom}
      marginLeft={marginLeft}
      marginRight={marginRight}
      height={height}
      width={width}
      mobileHeight={mobileHeight}
      mobileWidth={mobileWidth}
      device={device}
    >
      <Skeleton height='100%' />
    </Container>
  );
};

export default DynamicAdSlot;
